import type { ReactNode } from 'react'
import sparkyIcon from '@/assets/brand/sparky_frame1.png'
import { Surface } from '@/components/ui/surface'
import { cn } from '@/lib/utils'

export interface EmptyStateProps {
  /** Dutch heading that says what is missing, e.g. "Nog geen notities". */
  title: string
  /** One line (±60 characters) on what this place gives you and what it needs. */
  description?: string
  /** The primary action of the empty place; a header button stays, as an outline. */
  action?: ReactNode
  /**
   * `card` (default) brings its own quiet frame, for a free-standing empty page or tab;
   * `bare` has none, for use inside a `.vonk-card` or `.vonk-subcard` (no frame in a frame).
   */
  variant?: 'card' | 'bare'
  /** `compact`: a small mascot and tighter spacing, for lists, tables and side cards. */
  size?: 'default' | 'compact'
  /** Leave Sparky out (several empty lists side by side, such as the goal pillars). */
  mascot?: boolean
  className?: string
}

/**
 * The one empty state (spec-64-u2, U-095 / DS-13): Sparky, a Dutch title, one line and
 * the next step. The mascot is decoration (`alt=""`); the title says it all. The grid with
 * `justify-items-center` keeps the text at full width instead of a narrow column.
 */
export function EmptyState({
  title,
  description,
  action,
  variant = 'card',
  size = 'default',
  mascot = true,
  className,
}: EmptyStateProps) {
  const compact = size === 'compact'
  const body = (
    <>
      {mascot ? (
        <img
          src={sparkyIcon}
          alt=""
          data-slot="empty-state-mascot"
          className={cn('object-contain', compact ? 'size-10' : 'size-16')}
        />
      ) : null}
      <p className={cn('font-heading font-semibold text-foreground', compact ? 'text-sm' : 'text-base')}>
        {title}
      </p>
      {description ? <p className="max-w-prose text-sm text-muted-foreground">{description}</p> : null}
      {action ? <div className={compact ? 'mt-1' : 'mt-2'}>{action}</div> : null}
    </>
  )
  const layout = cn(
    'grid grid-cols-1 justify-items-center gap-2 text-center',
    compact ? 'py-4' : 'py-8',
    className,
  )
  if (variant === 'bare') {
    return (
      <div data-slot="empty-state" data-variant="bare" className={layout}>
        {body}
      </div>
    )
  }
  return (
    <Surface variant="inset" padding="md" data-slot="empty-state" data-variant="card" className={layout}>
      {body}
    </Surface>
  )
}
